import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import React from "react";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faFileLines, faPlus, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { usePatientEncountersInfinite } from "@/hooks/encounters";
import { useOutboundConfig } from "@/hooks/outbound";
import { StateView } from "@/components/state-view";
import { Button } from "@/components/button";
import { isNotFoundError } from "@/utils/http";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { DateRangeFilter, FilterBar, MultiSelectFilter, toIsoDateRange } from "@/components/filters";
import { EncountersTable } from "./encounters-table";
import { EncountersSkeleton } from "./encounters-skeleton";
import { MedicalReportModal } from "./medical-report-modal";
import { BidirectionalSync } from "./bidirectional-sync";
import { EncounterFlowModal } from "./encounter-flow";
import { useSelectedDocument } from "./use-selected-document";
import { ENCOUNTER_STATUS_GROUPS } from "./encounter-status-options";

/** Active create/update flow target. */

/** Records panel: filterable clinical encounters and their viewable documents. */
export function EncountersPanel(_ref) {
  var _encounters$data$page2, _encounters$data2;
  var workspaceId = _ref.workspaceId,
    patientId = _ref.patientId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState([null, null]),
    dateRange = _useState[0],
    setDateRange = _useState[1];
  var _useState2 = useState([]),
    statusValues = _useState2[0],
    setStatusValues = _useState2[1];
  var _useState3 = useState({}),
    applied = _useState3[0],
    setApplied = _useState3[1];
  var _useState4 = useState(null),
    flow = _useState4[0],
    setFlow = _useState4[1];
  var _useState5 = useState(false),
    configRequested = _useState5[0],
    setConfigRequested = _useState5[1];
  var draftFilters = useMemo(function () {
    return _objectSpread(_objectSpread({}, toIsoDateRange(dateRange)), statusValues.length ? {
      status: statusValues.join(",")
    } : {});
  }, [dateRange, statusValues]);
  var encounters = usePatientEncountersInfinite(workspaceId, patientId, applied);
  var outbound = useOutboundConfig(workspaceId, configRequested);
  var document = useSelectedDocument(workspaceId, patientId);
  var openFlow = function openFlow(next) {
    setConfigRequested(true);
    setFlow(next);
  };
  var rows = useMemo(function () {
    var _encounters$data$page, _encounters$data;
    return (_encounters$data$page = (_encounters$data = encounters.data) === null || _encounters$data === void 0 || (_encounters$data = _encounters$data.pages) === null || _encounters$data === void 0 ? void 0 : _encounters$data.flatMap(function (page) {
      return page.encounters;
    })) !== null && _encounters$data$page !== void 0 ? _encounters$data$page : [];
  }, [encounters.data]);
  var totalCount = (_encounters$data$page2 = (_encounters$data2 = encounters.data) === null || _encounters$data2 === void 0 || (_encounters$data2 = _encounters$data2.pages) === null || _encounters$data2 === void 0 || (_encounters$data2 = _encounters$data2[0]) === null || _encounters$data2 === void 0 ? void 0 : _encounters$data2.total_count) !== null && _encounters$data$page2 !== void 0 ? _encounters$data$page2 : rows.length;
  var notFound = isNotFoundError(encounters.error);
  var statusOptions = ENCOUNTER_STATUS_GROUPS.map(function (group) {
    return {
      label: t(group.labelKey, {
        defaultValue: group.fallback
      }),
      value: group.value
    };
  });
  var hasDraft = statusValues.length > 0 || Boolean(dateRange[0] && dateRange[1]);
  var canClear = hasDraft || Object.keys(applied).length > 0;
  var clearFilters = function clearFilters() {
    setDateRange([null, null]);
    setStatusValues([]);
    setApplied({});
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex h-full flex-col"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-shrink-0 items-center gap-3 p-5 pb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-1"
  }, /*#__PURE__*/React.createElement(FilterBar, {
    onApply: function onApply() {
      return setApplied(draftFilters);
    },
    onClear: clearFilters,
    canApply: JSON.stringify(draftFilters) !== JSON.stringify(applied),
    canClear: canClear
  }, /*#__PURE__*/React.createElement(DateRangeFilter, {
    label: t("date_range", {
      defaultValue: "Date range"
    }),
    value: dateRange,
    onChange: setDateRange,
    testId: "encounter-date-filter"
  }), /*#__PURE__*/React.createElement(MultiSelectFilter, {
    label: t("status", {
      defaultValue: "Status"
    }),
    options: statusOptions,
    selected: statusValues,
    onChange: setStatusValues,
    testId: "encounter-status-filter"
  }))), /*#__PURE__*/React.createElement(BidirectionalSync, {
    workspaceId: workspaceId,
    patientId: patientId
  }), /*#__PURE__*/React.createElement(Button, {
    leftIcon: faPlus,
    "data-testid": "create-encounter-open",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.CREATE_ENCOUNTER_OPEN, {
        patientId: patientId
      });
      openFlow({
        action: "create"
      });
    }
  }, t("create_encounter", {
    defaultValue: "Create encounter"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "min-h-0 flex-1 overflow-y-auto scrollbar-sds px-5 pb-5"
  }, encounters.isLoading ? /*#__PURE__*/React.createElement(EncountersSkeleton, null) : encounters.isError && !notFound ? /*#__PURE__*/React.createElement(StateView, {
    intent: "urgent",
    icon: faTriangleExclamation,
    title: t("failed_to_load", {
      defaultValue: "Couldn't load encounters"
    }),
    description: t("failed_to_load_desc", {
      defaultValue: "Something went wrong while loading this section."
    }),
    onRetry: function onRetry() {
      return encounters.refetch();
    }
  }) : !rows.length || notFound ? /*#__PURE__*/React.createElement(StateView, {
    icon: faFileLines,
    title: t("no_encounters_found", {
      defaultValue: "No encounters found"
    }),
    description: Object.keys(applied).length ? t("no_encounters_filtered", {
      defaultValue: "No encounters match the selected filters."
    }) : t("no_encounters_desc", {
      defaultValue: "This patient has no recorded encounters."
    })
  }) : /*#__PURE__*/React.createElement(EncountersTable, {
    rows: rows,
    totalCount: totalCount,
    onOpenDocument: document.openDocument,
    isOpening: document.isOpening,
    hasMore: Boolean(encounters.hasNextPage),
    isLoadingMore: encounters.isFetchingNextPage,
    onLoadMore: encounters.fetchNextPage,
    onAddNote: function onAddNote(encounter) {
      return openFlow({
        action: "update",
        encounter: encounter
      });
    }
  })), document.selectedDocument ? /*#__PURE__*/React.createElement(MedicalReportModal, {
    show: true,
    onClose: document.closeDocument,
    document: document.selectedDocument
  }) : null, flow ? /*#__PURE__*/React.createElement(EncounterFlowModal, {
    action: flow.action,
    encounter: flow.encounter,
    encounterConfig: outbound.encounterConfig,
    encounterMeta: outbound.encounterMeta,
    docConfig: outbound.docCreationConfig,
    docMeta: outbound.docCreationMeta,
    workspaceId: workspaceId,
    patientId: patientId,
    isLoading: outbound.isLoading,
    onClose: function onClose() {
      return setFlow(null);
    }
  }) : null);
}